import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// [&_svg:not([class*='size-'])]:size-5
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none  [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 disabled:bg-button-secondary disabled:text-foreground",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        "outline-black": "border border-black/90 bg-background hover:bg-black/90 hover:text-accent-foreground",
        secondary: "bg-button-secondary text-accent-foreground shadow-none hover:bg-button-secondary-hover",
        ghost: "hover:bg-card-hover hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        shadow: "bg-background shadow-button hover:bg-background/50",
        dashed: "border-2 border-dashed border-input bg-background hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 text-[16px] rounded-[10px] px-6 has-[>svg]:px-4",
        xl: "h-12.5 rounded-md px-8 text-lg has-[>svg]:px-6 [&_svg]:!size-5",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

function Button({ className, variant, size, asChild = false, loading = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp data-slot="button" {...props} className={cn(buttonVariants({ variant, size, className }))} disabled={loading || props.disabled}>
      {loading ? <IconFluentSpinnerIos16Filled className="animate-spin" /> : props.children}
    </Comp>
  )
}

export { Button, buttonVariants }
